// src/components/SemanticChat.jsx
import React, { useState, useEffect, useRef, useMemo, useCallback, memo } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box, Paper, Typography, Button, Chip, TextField, Avatar, Stack,
  Accordion, AccordionSummary, AccordionDetails, LinearProgress,
  IconButton, Tooltip, Collapse, Fade, Alert, Divider, Badge,
  List, ListItem, ListItemIcon, ListItemText, ListItemButton,
  Popper, ClickAwayListener, MenuItem, Menu, CircularProgress,
  Switch, FormControlLabel
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  Chat as ChatIcon,
  Send as SendIcon,
  Psychology as PsychologyIcon,
  HelpOutline as HelpOutlineIcon,
  EmojiEmotions as EmojiEmotionsIcon,
  Lightbulb as LightbulbIcon,
  AutoAwesome as AutoAwesomeIcon,
  Flag as FlagIcon,
  Task as TaskIcon,
  Timeline as TimelineIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Terminal as TerminalIcon,
  GpsFixed as GpsFixedIcon,
  Explore as ExploreIcon,
  RestartAlt as RestartAltIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  SettingsVoice as SettingsVoiceIcon,
  Speed as SpeedIcon,
  Memory as MemoryIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';

import { addChatMessage } from '../../store/slices/gameSlice';

// ============================================================================
// 🎯 КОМПОНЕНТ ПОТОКА МЫСЛЕЙ (С ИСПРАВЛЕНИЕМ)
// ============================================================================

const ThoughtStream = memo(({ 
  thoughts = [], 
  onThoughtClick, 
  activeThoughtId, 
  isLoading,
  maxDisplay = 10,
  filterTypes = [],
  showImportance = true,
  compact = false
}) => {
  const streamEndRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [localFilter, setLocalFilter] = useState([]);

  useEffect(() => {
    streamEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [thoughts]);

  const getTypeColor = (type) => {
    const colors = {
      analysis: '#3b82f6',
      status: '#10b981',
      reflection: '#8b5cf6',
      decision: '#f59e0b',
      emotion: '#ef4444',
      question: '#ec4899',
      insight: '#06b6d4',
      goal: '#22c55e',
      warning: '#f97316',
      perception: '#14b8a6',
      memory: '#f472b6',
      planning: '#a78bfa',
      dialogue: '#34d399',
      system: '#6b7280'
    };
    return colors[type] || '#6b7280';
  };

  const getTypeIcon = (type) => {
    const icons = {
      analysis: <TimelineIcon fontSize="inherit" />,
      status: <CheckCircleIcon fontSize="inherit" />,
      reflection: <PsychologyIcon fontSize="inherit" />,
      decision: <FlagIcon fontSize="inherit" />,
      emotion: <EmojiEmotionsIcon fontSize="inherit" />,
      question: <HelpOutlineIcon fontSize="inherit" />,
      insight: <LightbulbIcon fontSize="inherit" />,
      goal: <FlagIcon fontSize="inherit" />,
      warning: <WarningIcon fontSize="inherit" />,
      perception: <ExploreIcon fontSize="inherit" />,
      memory: <MemoryIcon fontSize="inherit" />,
      planning: <AutoAwesomeIcon fontSize="inherit" />,
      dialogue: <ChatIcon fontSize="inherit" />,
      system: <TerminalIcon fontSize="inherit" />
    };
    return icons[type] || <PsychologyIcon fontSize="inherit" />;
  };

  const getTypeLabel = (type) => {
    const labels = {
      analysis: 'Анализ',
      status: 'Статус',
      reflection: 'Размышление',
      decision: 'Решение',
      emotion: 'Эмоция',
      question: 'Вопрос',
      insight: 'Инсайт',
      goal: 'Цель',
      warning: 'Предупреждение',
      perception: 'Восприятие',
      memory: 'Память',
      planning: 'Планирование',
      dialogue: 'Диалог',
      system: 'Система'
    };
    return labels[type] || type;
  };

  // 🔧 ИСПРАВЛЕННАЯ ФИЛЬТРАЦИЯ - создаем копию массива!
  const filteredThoughts = useMemo(() => {
    // Создаем копию массива, чтобы не мутировать исходный (замороженный Redux)
    let result = [...thoughts];
    
    // Фильтр по типам
    if (localFilter.length > 0) {
      result = result.filter(t => localFilter.includes(t.type));
    }
    
    // Сортировка по важности и времени (на копии)
    return result
      .sort((a, b) => {
        const aImportance = a.importance || 0;
        const bImportance = b.importance || 0;
        if (aImportance !== bImportance) return bImportance - aImportance;
        return (b.timestamp || 0) - (a.timestamp || 0);
      })
      .slice(0, expanded ? 50 : maxDisplay);
  }, [thoughts, localFilter, maxDisplay, expanded]);

  // Типы мыслей для фильтрации
  const availableTypes = useMemo(() => {
    const types = new Set();
    thoughts.forEach(t => {
      if (t.type) types.add(t.type);
    });
    return Array.from(types);
  }, [thoughts]);

  const toggleFilter = (type) => {
    setLocalFilter(prev => 
      prev.includes(type) 
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (!thoughts || thoughts.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
          Нет активных мыслей
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Фильтры */}
      {availableTypes.length > 1 && (
        <Box sx={{ display: 'flex', gap: 0.25, flexWrap: 'wrap', mb: 0.5 }}>
          {availableTypes.map(type => (
            <Chip
              key={type}
              label={getTypeLabel(type)}
              size="small"
              onClick={() => toggleFilter(type)}
              sx={{
                height: 14,
                '& .MuiChip-label': { fontSize: '0.35rem', px: 0.5 },
                bgcolor: localFilter.includes(type) 
                  ? `${getTypeColor(type)}30` 
                  : 'rgba(255,255,255,0.03)',
                borderColor: localFilter.includes(type) 
                  ? getTypeColor(type) 
                  : 'rgba(255,255,255,0.05)',
                cursor: 'pointer'
              }}
              variant={localFilter.includes(type) ? 'filled' : 'outlined'}
            />
          ))}
          {localFilter.length > 0 && (
            <Chip
              label="Сбросить"
              size="small"
              onClick={() => setLocalFilter([])}
              sx={{
                height: 14,
                '& .MuiChip-label': { fontSize: '0.35rem', px: 0.5 },
                bgcolor: 'rgba(239, 68, 68, 0.1)',
                color: '#ef4444',
                cursor: 'pointer'
              }}
            />
          )}
        </Box>
      )}

      <Stack spacing={0.5}>
        {filteredThoughts.map((thought, idx) => {
          const isActive = thought.id === activeThoughtId;
          const color = getTypeColor(thought.type);
          
          return (
            <Fade in key={thought.id || idx} timeout={150}>
              <Box
                sx={{
                  p: compact ? 0.5 : 0.75,
                  borderRadius: 1.5,
                  bgcolor: isActive ? `${color}15` : 'rgba(255,255,255,0.02)',
                  border: isActive ? `1px solid ${color}30` : '1px solid rgba(255,255,255,0.03)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: `${color}08`,
                    borderColor: `${color}20`
                  }
                }}
                onClick={() => onThoughtClick?.(thought)}
              >
                <Stack direction="row" spacing={0.75} alignItems="flex-start">
                  <Avatar
                    sx={{
                      width: compact ? 16 : 20,
                      height: compact ? 16 : 20,
                      bgcolor: `${color}20`,
                      color: color,
                      fontSize: compact ? 10 : 12,
                      flexShrink: 0,
                      mt: 0.1
                    }}
                  >
                    {getTypeIcon(thought.type)}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        fontSize: compact ? '0.55rem' : '0.65rem', 
                        lineHeight: 1.3, 
                        wordBreak: 'break-word',
                        opacity: thought.importance < 0.3 ? 0.6 : 1
                      }}
                    >
                      {thought.text}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25, flexWrap: 'wrap' }}>
                      <Chip
                        label={getTypeLabel(thought.type)}
                        size="small"
                        sx={{
                          height: compact ? 12 : 14,
                          bgcolor: `${color}15`,
                          color: color,
                          '& .MuiChip-label': { fontSize: compact ? '0.35rem' : '0.4rem', px: 0.5 }
                        }}
                      />
                      {showImportance && thought.importance !== undefined && thought.importance !== null && (
                        <Chip
                          label={`${Math.round(thought.importance * 100)}%`}
                          size="small"
                          variant="outlined"
                          sx={{
                            height: compact ? 12 : 14,
                            borderColor: thought.importance > 0.7 ? 'rgba(239, 68, 68, 0.3)' : 'rgba(255,255,255,0.1)',
                            '& .MuiChip-label': { fontSize: compact ? '0.35rem' : '0.4rem', px: 0.5 }
                          }}
                        />
                      )}
                      {thought.timestamp && (
                        <Typography variant="caption" sx={{ fontSize: compact ? '0.3rem' : '0.35rem', color: 'text.secondary', ml: 'auto' }}>
                          {new Date(thought.timestamp * 1000).toLocaleTimeString()}
                        </Typography>
                      )}
                      {thought.emotion && (
                        <Chip
                          label={`😊 ${thought.emotion}`}
                          size="small"
                          sx={{
                            height: compact ? 12 : 14,
                            '& .MuiChip-label': { fontSize: compact ? '0.35rem' : '0.4rem', px: 0.5 }
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                </Stack>
              </Box>
            </Fade>
          );
        })}
      </Stack>

      {thoughts.length > maxDisplay && !expanded && (
        <Box sx={{ textAlign: 'center', mt: 0.5 }}>
          <Button
            size="small"
            onClick={() => setExpanded(true)}
            sx={{ 
              fontSize: '0.5rem', 
              py: 0.25,
              color: 'text.secondary'
            }}
          >
            Показать ещё ({thoughts.length - maxDisplay})
          </Button>
        </Box>
      )}
      {expanded && thoughts.length > maxDisplay && (
        <Box sx={{ textAlign: 'center', mt: 0.5 }}>
          <Button
            size="small"
            onClick={() => setExpanded(false)}
            sx={{ 
              fontSize: '0.5rem', 
              py: 0.25,
              color: 'text.secondary'
            }}
          >
            Свернуть
          </Button>
        </Box>
      )}

      <div ref={streamEndRef} />
    </Box>
  );
}, (prevProps, nextProps) => {
  // Кастомное сравнение для предотвращения лишних перерисовок
  return prevProps.thoughts === nextProps.thoughts &&
         prevProps.activeThoughtId === nextProps.activeThoughtId &&
         prevProps.isLoading === nextProps.isLoading &&
         prevProps.maxDisplay === nextProps.maxDisplay &&
         prevProps.compact === nextProps.compact;
});

// ============================================================================
// 🎯 КОМПОНЕНТ ВНУТРЕННЕГО ДИАЛОГА
// ============================================================================

const InnerDialog = ({ 
  messages = [], 
  characterName, 
  isLoading,
  maxDisplay = 50,
  showTimestamps = true,
  compact = false
}) => {
  const dialogEndRef = useRef(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    dialogEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const displayMessages = useMemo(() => {
    return expanded ? messages : messages.slice(-maxDisplay);
  }, [messages, maxDisplay, expanded]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (messages.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
          Начните диалог с персонажем...
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {messages.length > maxDisplay && !expanded && (
        <Box sx={{ textAlign: 'center', mb: 0.5 }}>
          <Chip
            label={`Ещё ${messages.length - maxDisplay} сообщений`}
            size="small"
            onClick={() => setExpanded(true)}
            sx={{ 
              cursor: 'pointer',
              height: 16,
              '& .MuiChip-label': { fontSize: '0.45rem' }
            }}
          />
        </Box>
      )}

      <Stack spacing={0.5}>
        {displayMessages.map((msg, idx) => {
          const isCharacter = msg.sender === 'character' || msg.sender === 'ai';
          const isPlayer = msg.sender === 'player' || msg.sender === 'user';
          const isSystem = msg.sender === 'system';

          return (
            <Fade in key={idx} timeout={200}>
              <Box
                sx={{
                  alignSelf: isPlayer ? 'flex-end' : 'flex-start',
                  maxWidth: isPlayer ? '85%' : '92%',
                  p: compact ? 0.5 : 0.75,
                  borderRadius: 1,
                  bgcolor: isPlayer 
                    ? 'rgba(99, 102, 241, 0.15)' 
                    : isSystem 
                      ? 'rgba(255, 255, 255, 0.03)' 
                      : 'rgba(139, 92, 246, 0.08)',
                  border: isCharacter 
                    ? '1px solid rgba(139, 92, 246, 0.15)' 
                    : 'none',
                  position: 'relative'
                }}
              >
                <Stack direction="row" spacing={0.5} alignItems="flex-start">
                  {isCharacter && (
                    <Avatar sx={{ 
                      width: compact ? 14 : 16, 
                      height: compact ? 14 : 16, 
                      bgcolor: 'rgba(139, 92, 246, 0.15)', 
                      color: '#8b5cf6', 
                      fontSize: compact ? 8 : 9,
                      flexShrink: 0
                    }}>
                      <PsychologyIcon fontSize="inherit" />
                    </Avatar>
                  )}
                  {isSystem && (
                    <Avatar sx={{ 
                      width: compact ? 14 : 16, 
                      height: compact ? 14 : 16, 
                      bgcolor: 'rgba(255, 255, 255, 0.05)', 
                      color: 'text.secondary', 
                      fontSize: compact ? 8 : 9,
                      flexShrink: 0
                    }}>
                      <TerminalIcon fontSize="inherit" />
                    </Avatar>
                  )}
                  {isPlayer && (
                    <Avatar sx={{ 
                      width: compact ? 14 : 16, 
                      height: compact ? 14 : 16, 
                      bgcolor: 'rgba(99, 102, 241, 0.15)', 
                      color: '#6366f1', 
                      fontSize: compact ? 8 : 9,
                      flexShrink: 0
                    }}>
                      <SettingsVoiceIcon fontSize="inherit" />
                    </Avatar>
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontSize: compact ? '0.6rem' : '0.7rem', 
                        wordBreak: 'break-word',
                        whiteSpace: 'pre-wrap'
                      }}
                    >
                      {msg.text}
                    </Typography>
                    {isCharacter && (
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25, flexWrap: 'wrap' }}>
                        {msg.emotion !== undefined && msg.emotion !== null && (
                          <Chip
                            label={`😊 ${Math.round(msg.emotion * 100)}%`}
                            size="small"
                            sx={{ 
                              height: compact ? 12 : 14, 
                              '& .MuiChip-label': { fontSize: compact ? '0.35rem' : '0.4rem', px: 0.5 } 
                            }}
                          />
                        )}
                        {msg.confidence !== undefined && msg.confidence !== null && (
                          <Chip
                            label={`🎯 ${Math.round(msg.confidence * 100)}%`}
                            size="small"
                            variant="outlined"
                            sx={{ 
                              height: compact ? 12 : 14, 
                              '& .MuiChip-label': { fontSize: compact ? '0.35rem' : '0.4rem', px: 0.5 } 
                            }}
                          />
                        )}
                        {msg.type && (
                          <Chip
                            label={msg.type}
                            size="small"
                            variant="outlined"
                            sx={{ 
                              height: compact ? 12 : 14, 
                              '& .MuiChip-label': { fontSize: compact ? '0.35rem' : '0.4rem', px: 0.5 } 
                            }}
                          />
                        )}
                      </Box>
                    )}
                    {showTimestamps && (
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          display: 'block', 
                          textAlign: 'right', 
                          opacity: 0.3, 
                          mt: 0.1, 
                          fontSize: compact ? '0.3rem' : '0.4rem' 
                        }}
                      >
                        {msg.time || new Date().toLocaleTimeString()}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </Box>
            </Fade>
          );
        })}
      </Stack>

      {expanded && messages.length > maxDisplay && (
        <Box sx={{ textAlign: 'center', mt: 0.5 }}>
          <Button
            size="small"
            onClick={() => setExpanded(false)}
            sx={{ 
              fontSize: '0.5rem', 
              py: 0.25,
              color: 'text.secondary'
            }}
          >
            Свернуть
          </Button>
        </Box>
      )}

      <div ref={dialogEndRef} />
    </>
  );
};

// ============================================================================
// 🎯 ПАНЕЛЬ ПОДСКАЗОК
// ============================================================================

const SuggestionPanel = ({ suggestions = [], onSelect, input }) => {
  const [filtered, setFiltered] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  useEffect(() => {
    if (input && input.startsWith('/')) {
      const query = input.slice(1).toLowerCase();
      const matches = suggestions.filter(s => 
        s.cmd.toLowerCase().includes(query) ||
        s.description.toLowerCase().includes(query) ||
        s.category?.toLowerCase().includes(query)
      );
      setFiltered(matches.slice(0, 8));
      setSelectedIndex(-1);
    } else {
      setFiltered([]);
    }
  }, [input, suggestions]);

  const handleSelect = (suggestion) => {
    onSelect?.(suggestion);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault();
      handleSelect(filtered[selectedIndex]);
    }
  };

  if (filtered.length === 0) return null;

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'absolute',
        bottom: '100%',
        left: 0,
        right: 0,
        mb: 0.5,
        maxHeight: 200,
        overflow: 'auto',
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        zIndex: 1300,
        boxShadow: '0 8px 32px rgba(0,0,0,0.4)'
      }}
      onKeyDown={handleKeyDown}
    >
      <List dense disablePadding>
        {filtered.map((suggestion, idx) => (
          <ListItemButton
            key={idx}
            selected={idx === selectedIndex}
            onClick={() => handleSelect(suggestion)}
            sx={{
              py: 0.5,
              '&.Mui-selected': {
                bgcolor: theme => `${theme.palette.primary.main}15`
              },
              '&:hover': {
                bgcolor: theme => `${theme.palette.primary.main}08`
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: 28 }}>
              {suggestion.icon || <HelpOutlineIcon fontSize="small" color="primary" />}
            </ListItemIcon>
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '0.65rem' }}>
                    {suggestion.cmd}
                  </Typography>
                  <Chip
                    label={suggestion.category || 'команда'}
                    size="small"
                    sx={{ height: 14, '& .MuiChip-label': { fontSize: '0.4rem', px: 0.5 } }}
                  />
                  {suggestion.example && (
                    <Typography variant="caption" sx={{ fontSize: '0.4rem', color: 'text.secondary' }}>
                      {suggestion.example}
                    </Typography>
                  )}
                </Box>
              }
              secondary={suggestion.description}
              secondaryTypographyProps={{ variant: 'caption', sx: { fontSize: '0.55rem' } }}
            />
          </ListItemButton>
        ))}
      </List>
    </Paper>
  );
};

// ============================================================================
// 🎯 ОСНОВНОЙ КОМПОНЕНТ
// ============================================================================

const SemanticChat = ({
  emotions = {},
  goals = [],
  tasks = [],
  intents = [],
  consciousness = {},
  onSendMessage,
  messages = [],
  dispatch,
  questions = [],
  onAnswerQuestion,
  isConnected,
  characterId,
  characterName,
  loadDetailedTasks,
  loadEmotions,
  loadGoals,
  loadTasks,
  loadIntents,
  loadConsciousness,
  refreshState,
  getThoughtStream,
  sendDialogue,
  sendSuggestion,
  thoughtStream = [],
  isThoughtsLoading = false,
  onCommand,
  autoRefresh = true,
  refreshInterval = 5000
}) => {
  const [input, setInput] = useState('');
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [activeThoughtId, setActiveThoughtId] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [innerDialogMessages, setInnerDialogMessages] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [thoughts, setThoughts] = useState([]);
  const [isAutoRefresh, setIsAutoRefresh] = useState(autoRefresh);
  const [showTimestamps, setShowTimestamps] = useState(true);
  const [compactMode, setCompactMode] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);
  const refreshTimerRef = useRef(null);

  // Команды-подсказки для персонажа
  const SUGGESTIONS = [
    { 
      cmd: '/suggest', 
      description: 'Дать персонажу подсказку или идею',
      category: 'подсказка',
      icon: <LightbulbIcon fontSize="small" />,
      example: '/suggest "осмотреть комнату"'
    },
    { 
      cmd: '/focus', 
      description: 'Сменить фокус внимания персонажа',
      category: 'фокус',
      icon: <GpsFixedIcon fontSize="small" />,
      example: '/focus "разговор"'
    },
    { 
      cmd: '/emotion', 
      description: 'Внушить эмоцию (имя уровень 0-1)',
      category: 'эмоция',
      icon: <EmojiEmotionsIcon fontSize="small" />,
      example: '/emotion радость 0.8'
    },
    { 
      cmd: '/goal', 
      description: 'Предложить новую цель персонажу',
      category: 'цель',
      icon: <FlagIcon fontSize="small" />,
      example: '/goal "найти выход"'
    },
    { 
      cmd: '/task', 
      description: 'Добавить задачу персонажу',
      category: 'задача',
      icon: <TaskIcon fontSize="small" />,
      example: '/task "собрать информацию"'
    },
    { 
      cmd: '/reflect', 
      description: 'Заставить персонажа задуматься о чём-то',
      category: 'рефлексия',
      icon: <PsychologyIcon fontSize="small" />,
      example: '/reflect "свои действия"'
    },
    { 
      cmd: '/decision', 
      description: 'Помочь персонажу принять решение',
      category: 'решение',
      icon: <AutoAwesomeIcon fontSize="small" />,
      example: '/decision "идти налево или направо"'
    },
    { 
      cmd: '/memory', 
      description: 'Напомнить персонажу о чём-то',
      category: 'память',
      icon: <MemoryIcon fontSize="small" />,
      example: '/memory "встречу с другом"'
    },
    { 
      cmd: '/status', 
      description: 'Запросить полный статус персонажа',
      category: 'статус',
      icon: <CheckCircleIcon fontSize="small" />
    },
    { 
      cmd: '/clear', 
      description: 'Очистить диалоговую историю',
      category: 'система',
      icon: <CloseIcon fontSize="small" />
    },
    { 
      cmd: '/help', 
      description: 'Показать все доступные команды',
      category: 'система',
      icon: <HelpOutlineIcon fontSize="small" />
    },
    { 
      cmd: '/pause', 
      description: 'Приостановить автономное поведение',
      category: 'управление',
      icon: <PauseIcon fontSize="small" />
    },
    { 
      cmd: '/resume', 
      description: 'Возобновить автономное поведение',
      category: 'управление',
      icon: <PlayArrowIcon fontSize="small" />
    },
    { 
      cmd: '/speed', 
      description: 'Изменить скорость мышления (1-10)',
      category: 'управление',
      icon: <SpeedIcon fontSize="small" />,
      example: '/speed 5'
    },
  ];

  // 🔧 НАКОПЛЕНИЕ ИСТОРИИ МЫСЛЕЙ (без мерцания)
  useEffect(() => {
    if (thoughtStream && thoughtStream.length > 0) {
      setThoughts(prev => {
        // Если пришли те же мысли - не обновляем
        if (prev.length === thoughtStream.length && 
            prev.every((t, i) => t.id === thoughtStream[i]?.id)) {
          return prev;
        }
        
        // Создаем Map для уникальных ID
        const existingMap = new Map();
        prev.forEach(t => {
          if (t.id) existingMap.set(t.id, t);
        });
        
        // Добавляем новые мысли
        let hasNew = false;
        thoughtStream.forEach(t => {
          if (t.id && !existingMap.has(t.id)) {
            existingMap.set(t.id, t);
            hasNew = true;
          }
        });
        
        // Если нет новых мыслей - возвращаем предыдущее состояние
        if (!hasNew) return prev;
        
        // Сортируем по времени и ограничиваем 50
        return Array.from(existingMap.values())
          .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
          .slice(0, 50);
      });
    }
  }, [thoughtStream]);

  // Автоматическое обновление
  useEffect(() => {
    if (isConnected && isAutoRefresh && getThoughtStream && characterId) {
      const loadThoughts = async () => {
        try {
          await getThoughtStream(characterId);
          setLastRefresh(Date.now());
        } catch (error) {
          console.error('Failed to load thoughts:', error);
        }
      };

      loadThoughts();
      refreshTimerRef.current = setInterval(loadThoughts, refreshInterval);
      return () => clearInterval(refreshTimerRef.current);
    }
  }, [characterId, isConnected, getThoughtStream, isAutoRefresh, refreshInterval]);

  /**
   * 🔧 ОСНОВНАЯ ФУНКЦИЯ ОТПРАВКИ СООБЩЕНИЯ
   * Обрабатывает как обычные сообщения, так и команды (/...)
   */
  const handleSend = useCallback(async () => {
    if (!input.trim() || !characterId || isProcessing) return;
    
    const time = new Date().toLocaleTimeString();
    const userMessage = input.trim();
    
    // ========================================================================
    // 🔧 ОБРАБОТКА КОМАНД (/...)
    // ========================================================================
    if (userMessage.startsWith('/')) {
      // Локальные команды
      if (userMessage === '/clear') {
        handleClearDialog();
        setInput('');
        return;
      }
      if (userMessage === '/help') {
        handleShowHelp();
        setInput('');
        return;
      }
      if (userMessage === '/status') {
        handleRefresh();
        setInput('');
        return;
      }

      // Добавляем команду в диалог как сообщение игрока
      setInnerDialogMessages(prev => [...prev, {
        sender: 'player',
        text: userMessage,
        time
      }]);

      // Отправляем команду на сервер через sendDialogue
      try {
        setIsProcessing(true);
        
        if (sendDialogue) {
          const response = await sendDialogue(characterId, userMessage);
          
          if (response?.response) {
            const resp = response.response;
            setInnerDialogMessages(prev => [...prev, {
              sender: 'system',
              text: resp.text || 'Команда выполнена',
              time: new Date().toLocaleTimeString()
            }]);
            
            if (dispatch) {
              dispatch(addChatMessage({
                sender: 'system',
                text: resp.text || 'Команда выполнена',
                time: new Date().toLocaleTimeString()
              }));
            }
          } else {
            setInnerDialogMessages(prev => [...prev, {
              sender: 'system',
              text: `✅ Команда "${userMessage}" отправлена`,
              time: new Date().toLocaleTimeString()
            }]);
          }
        } else {
          setInnerDialogMessages(prev => [...prev, {
            sender: 'system',
            text: `⚠️ Команда "${userMessage}" не может быть отправлена (нет подключения)`,
            time: new Date().toLocaleTimeString()
          }]);
        }

        // Обновляем данные после команды
        setTimeout(() => {
          if (characterId) {
            if (loadEmotions) loadEmotions(characterId);
            if (loadGoals) loadGoals(characterId);
            if (loadTasks) loadTasks(characterId);
            if (loadIntents) loadIntents(characterId);
            if (loadConsciousness) loadConsciousness(characterId);
            if (loadDetailedTasks) loadDetailedTasks(characterId, true).catch(() => {});
            if (getThoughtStream) getThoughtStream(characterId);
          }
        }, 1000);

      } catch (error) {
        console.error('Command error:', error);
        setInnerDialogMessages(prev => [...prev, {
          sender: 'system',
          text: `❌ Ошибка команды: ${error.message || 'Неизвестная ошибка'}`,
          time: new Date().toLocaleTimeString()
        }]);
      } finally {
        setIsProcessing(false);
        setInput('');
      }
      return;
    }

    // ========================================================================
    // 🔧 ОБЫЧНОЕ СООБЩЕНИЕ (НЕ КОМАНДА)
    // ========================================================================
    
    // Добавляем сообщение игрока во внутренний диалог
    setInnerDialogMessages(prev => [...prev, {
      sender: 'player',
      text: userMessage,
      time
    }]);
    
    if (dispatch) {
      dispatch(addChatMessage({ 
        sender: 'user', 
        text: userMessage, 
        time 
      }));
    }
    
    setInput('');
    setIsProcessing(true);
    
    try {
      if (sendDialogue) {
        const response = await sendDialogue(characterId, userMessage);
        
        if (response && response.response) {
          const resp = response.response;
          setInnerDialogMessages(prev => [...prev, {
            sender: 'character',
            text: resp.text || '...',
            time: new Date().toLocaleTimeString(),
            emotion: resp.emotion,
            confidence: resp.confidence,
            type: resp.type
          }]);
          
          if (dispatch) {
            dispatch(addChatMessage({ 
              sender: 'ai', 
              text: resp.text || '...',
              time: new Date().toLocaleTimeString() 
            }));
          }
          
          // Обновляем данные после ответа
          setTimeout(() => {
            if (characterId) {
              if (loadEmotions) loadEmotions(characterId);
              if (loadGoals) loadGoals(characterId);
              if (loadTasks) loadTasks(characterId);
              if (loadIntents) loadIntents(characterId);
              if (loadConsciousness) loadConsciousness(characterId);
              if (loadDetailedTasks) loadDetailedTasks(characterId, true).catch(() => {});
              if (getThoughtStream) getThoughtStream(characterId);
            }
          }, 1000);
        }
      } else if (onSendMessage) {
        // Fallback на старый метод
        await onSendMessage({ text: userMessage, time });
      }
    } catch (error) {
      console.error('Failed to send dialogue:', error);
      setInnerDialogMessages(prev => [...prev, {
        sender: 'system',
        text: `❌ Ошибка: ${error.message || 'Неизвестная ошибка'}`,
        time: new Date().toLocaleTimeString()
      }]);
    } finally {
      setIsProcessing(false);
    }
  }, [
    input, 
    characterId, 
    isProcessing, 
    dispatch, 
    sendDialogue, 
    onSendMessage,
    loadEmotions, 
    loadGoals, 
    loadTasks, 
    loadIntents,
    loadConsciousness, 
    loadDetailedTasks, 
    getThoughtStream
  ]);

  /**
   * 🔧 ОБРАБОТЧИК ПОДСКАЗОК
   */
  const handleSuggestion = useCallback(async (command) => {
    if (!characterId || !sendSuggestion) return false;
    
    try {
      const response = await sendSuggestion(characterId, command);
      
      if (response && response.result) {
        const result = response.result;
        setInnerDialogMessages(prev => [...prev, {
          sender: 'system',
          text: `💡 ${result.result || 'Подсказка принята'}`,
          time: new Date().toLocaleTimeString()
        }]);
        
        // Обновляем данные после подсказки
        setTimeout(() => {
          if (characterId) {
            if (loadEmotions) loadEmotions(characterId);
            if (loadGoals) loadGoals(characterId);
            if (loadTasks) loadTasks(characterId);
            if (loadIntents) loadIntents(characterId);
            if (loadConsciousness) loadConsciousness(characterId);
            if (loadDetailedTasks) loadDetailedTasks(characterId, true).catch(() => {});
            if (getThoughtStream) getThoughtStream(characterId);
          }
        }, 1500);
        
        return true;
      }
    } catch (error) {
      console.error('Failed to send suggestion:', error);
      setInnerDialogMessages(prev => [...prev, {
        sender: 'system',
        text: `❌ Ошибка подсказки: ${error.message || 'Неизвестная ошибка'}`,
        time: new Date().toLocaleTimeString()
      }]);
    }
    return false;
  }, [characterId, sendSuggestion, loadEmotions, loadGoals, loadTasks, loadIntents, loadConsciousness, loadDetailedTasks, getThoughtStream]);

  /**
   * 🔧 ОБРАБОТЧИК КЛАВИШ
   */
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (selectedQuestion) {
        handleAnswerQuestion();
      } else {
        handleSend();
      }
    } else if (e.key === '/' && e.ctrlKey) {
      e.preventDefault();
      inputRef.current?.focus();
      setInput('/');
    } else if (e.key === 'Escape') {
      setSelectedQuestion(null);
      setShowSuggestions(false);
    }
  };

  /**
   * 🔧 ОТВЕТ НА ВОПРОС
   */
  const handleAnswerQuestion = () => {
    if (!selectedQuestion || !input.trim()) return;
    if (onAnswerQuestion) {
      onAnswerQuestion(selectedQuestion.id, input);
    }
    setInnerDialogMessages(prev => [...prev, {
      sender: 'player',
      text: `Ответ на вопрос: ${input}`,
      time: new Date().toLocaleTimeString()
    }]);
    setInput('');
    setSelectedQuestion(null);
  };

  /**
   * 🔧 ОЧИСТКА ДИАЛОГА
   */
  const handleClearDialog = () => {
    setInnerDialogMessages([]);
    if (dispatch) {
      dispatch(addChatMessage({ 
        sender: 'system', 
        text: '🧹 Диалог очищен',
        time: new Date().toLocaleTimeString() 
      }));
    }
  };

  /**
   * 🔧 ПОКАЗАТЬ ПОМОЩЬ
   */
  const handleShowHelp = () => {
    const helpText = SUGGESTIONS.map(s => `${s.cmd} — ${s.description}${s.example ? ` (${s.example})` : ''}`).join('\n');
    setInnerDialogMessages(prev => [...prev, {
      sender: 'system',
      text: `📋 Доступные команды:\n${helpText}`,
      time: new Date().toLocaleTimeString()
    }]);
  };

  /**
   * 🔧 ОБНОВЛЕНИЕ СОСТОЯНИЯ
   */
  const handleRefresh = async () => {
    if (refreshState) refreshState();
    if (characterId) {
      const promises = [];
      if (loadEmotions) promises.push(loadEmotions(characterId).catch(() => {}));
      if (loadGoals) promises.push(loadGoals(characterId).catch(() => {}));
      if (loadTasks) promises.push(loadTasks(characterId).catch(() => {}));
      if (loadIntents) promises.push(loadIntents(characterId).catch(() => {}));
      if (loadConsciousness) promises.push(loadConsciousness(characterId).catch(() => {}));
      if (loadDetailedTasks) promises.push(loadDetailedTasks(characterId, true).catch(() => {}));
      if (getThoughtStream) promises.push(getThoughtStream(characterId).catch(() => {}));
      await Promise.all(promises);
      setLastRefresh(Date.now());
    }
  };

  // Эффект для прокрутки
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [innerDialogMessages]);

  // Обработка выбора подсказки
  const handleSelectSuggestion = (suggestion) => {
    if (suggestion.cmd === '/help') {
      handleShowHelp();
      return;
    }
    if (suggestion.cmd === '/clear') {
      handleClearDialog();
      return;
    }
    if (suggestion.cmd === '/status') {
      handleRefresh();
      return;
    }
    setInput(suggestion.cmd + ' ');
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const hasQuestions = questions && Array.isArray(questions) && questions.length > 0;
  const isLoading = isThoughtsLoading || isProcessing;

  return (
    <Paper
      sx={{
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'rgba(0,0,0,0.3)',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: 2,
        overflow: 'hidden',
        flex: 1,
        minHeight: 0,
        height: '100%'
      }}
    >
      {/* Заголовок */}
      <Box
        sx={{
          p: compactMode ? 0.5 : 1,
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          flexShrink: 0,
          bgcolor: 'rgba(0,0,0,0.2)',
          flexWrap: 'wrap'
        }}
      >
        <PsychologyIcon sx={{ color: '#8b5cf6', fontSize: compactMode ? 14 : 16 }} />
        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: compactMode ? '0.6rem' : '0.7rem' }}>
          Внутренний диалог
        </Typography>
        
        {characterName && (
          <Chip
            label={characterName}
            size="small"
            sx={{
              height: compactMode ? 14 : 16,
              bgcolor: 'rgba(139, 92, 246, 0.1)',
              color: '#8b5cf6',
              '& .MuiChip-label': { fontSize: compactMode ? '0.4rem' : '0.5rem', px: 0.5 }
            }}
          />
        )}

        <Chip
          label={isConnected ? '🟢 Онлайн' : '🔴 Офлайн'}
          size="small"
          sx={{
            ml: 'auto',
            height: compactMode ? 14 : 16,
            '& .MuiChip-label': { fontSize: compactMode ? '0.4rem' : '0.45rem', px: 0.5 },
            bgcolor: isConnected ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
            color: isConnected ? '#22c55e' : '#ef4444'
          }}
        />

        {hasQuestions && (
          <Chip
            label={`${questions.length} вопросов`}
            size="small"
            color="warning"
            sx={{ height: compactMode ? 14 : 16, '& .MuiChip-label': { fontSize: compactMode ? '0.4rem' : '0.45rem', px: 0.5 } }}
          />
        )}

        <Badge badgeContent={thoughts.length} color="info" max={9}>
          <Chip
            label="Мысли"
            size="small"
            variant="outlined"
            sx={{ height: compactMode ? 14 : 16, '& .MuiChip-label': { fontSize: compactMode ? '0.4rem' : '0.45rem', px: 0.5 } }}
          />
        </Badge>

        <Tooltip title="Обновить состояние">
          <IconButton size="small" onClick={handleRefresh} sx={{ p: compactMode ? 0.15 : 0.25 }}>
            <RefreshIcon fontSize="small" sx={{ fontSize: compactMode ? 12 : 14 }} />
          </IconButton>
        </Tooltip>

        <Tooltip title="Очистить диалог">
          <IconButton size="small" onClick={handleClearDialog} sx={{ p: compactMode ? 0.15 : 0.25 }}>
            <CloseIcon fontSize="small" sx={{ fontSize: compactMode ? 12 : 14 }} />
          </IconButton>
        </Tooltip>

        <Tooltip title="Помощь">
          <IconButton size="small" onClick={handleShowHelp} sx={{ p: compactMode ? 0.15 : 0.25 }}>
            <HelpOutlineIcon fontSize="small" sx={{ fontSize: compactMode ? 12 : 14 }} />
          </IconButton>
        </Tooltip>

        <Tooltip title="Автообновление">
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={isAutoRefresh}
                onChange={(e) => setIsAutoRefresh(e.target.checked)}
                sx={{ m: 0 }}
              />
            }
            label=""
            sx={{ m: 0 }}
          />
        </Tooltip>

        <Tooltip title="Компактный режим">
          <IconButton 
            size="small" 
            onClick={() => setCompactMode(!compactMode)} 
            sx={{ p: compactMode ? 0.15 : 0.25 }}
          >
            {compactMode ? <ExpandLessIcon fontSize="small" sx={{ fontSize: compactMode ? 12 : 14 }} /> : <ExpandMoreIcon fontSize="small" sx={{ fontSize: compactMode ? 12 : 14 }} />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Основная область */}
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        
        {/* Левая панель - Поток мыслей */}
        <Box
          sx={{
            width: compactMode ? 160 : 200,
            flexShrink: 0,
            p: compactMode ? 0.5 : 1,
            borderRight: '1px solid rgba(255,255,255,0.06)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'rgba(0,0,0,0.15)'
          }}
        >
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              mb: 0.5,
              flexShrink: 0
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: compactMode ? '0.45rem' : '0.55rem' }}>
              🧠 Поток мыслей
            </Typography>
            <Chip 
              label={thoughts.length} 
              size="small" 
              sx={{ height: compactMode ? 12 : 14, '& .MuiChip-label': { fontSize: compactMode ? '0.35rem' : '0.4rem', px: 0.5 } }}
            />
          </Box>
          
          <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
            <ThoughtStream 
              thoughts={thoughts}
              activeThoughtId={activeThoughtId}
              isLoading={isThoughtsLoading}
              onThoughtClick={(thought) => setActiveThoughtId(thought.id)}
              maxDisplay={compactMode ? 6 : 10}
              compact={compactMode}
            />
          </Box>
        </Box>

        {/* Правая панель - Диалог */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            overflow: 'hidden'
          }}
        >
          {/* Область сообщений */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: compactMode ? 0.5 : 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5
            }}
          >
            {/* Индикатор статуса */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, flexShrink: 0 }}>
              {isProcessing && (
                <Chip
                  label="Обработка..."
                  size="small"
                  icon={<CircularProgress size={10} />}
                  sx={{ height: compactMode ? 14 : 16, '& .MuiChip-label': { fontSize: compactMode ? '0.4rem' : '0.45rem' } }}
                />
              )}
              {lastRefresh && isAutoRefresh && (
                <Typography variant="caption" sx={{ fontSize: compactMode ? '0.3rem' : '0.35rem', color: 'text.secondary', ml: 'auto' }}>
                  обновлено: {new Date(lastRefresh).toLocaleTimeString()}
                </Typography>
              )}
            </Box>

            {/* Вопросы от персонажа */}
            {hasQuestions && (
              <Accordion 
                sx={{ 
                  bgcolor: 'rgba(245, 158, 11, 0.05)', 
                  borderRadius: 1, 
                  '&:before': { display: 'none' },
                  border: '1px solid rgba(245, 158, 11, 0.1)',
                  flexShrink: 0
                }}
              >
                <AccordionSummary 
                  expandIcon={<ExpandMoreIcon sx={{ fontSize: compactMode ? 12 : 14 }} />} 
                  sx={{ 
                    minHeight: compactMode ? 22 : 28, 
                    py: 0, 
                    '& .MuiAccordionSummary-content': { my: compactMode ? 0.15 : 0.25 } 
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <HelpOutlineIcon sx={{ color: '#f59e0b', fontSize: compactMode ? 12 : 14 }} />
                    <Typography variant="caption" sx={{ color: '#f59e0b', fontWeight: 600, fontSize: compactMode ? '0.5rem' : '0.6rem' }}>
                      Вопросы ({questions.length})
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: compactMode ? 0.5 : 0.75 }}>
                  <Stack spacing={0.5}>
                    {questions.map((q) => (
                      <Box
                        key={q.id}
                        sx={{
                          p: compactMode ? 0.5 : 0.75,
                          borderRadius: 0.75,
                          bgcolor: selectedQuestion?.id === q.id ? 'rgba(245, 158, 11, 0.12)' : 'rgba(255,255,255,0.02)',
                          border: selectedQuestion?.id === q.id ? '1px solid rgba(245, 158, 11, 0.2)' : '1px solid rgba(255,255,255,0.03)',
                          cursor: 'pointer',
                          '&:hover': {
                            bgcolor: 'rgba(245, 158, 11, 0.08)',
                          }
                        }}
                        onClick={() => setSelectedQuestion(q)}
                      >
                        <Typography variant="caption" sx={{ fontSize: compactMode ? '0.5rem' : '0.6rem', display: 'block' }}>
                          {q.text}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                  {selectedQuestion && (
                    <Box sx={{ mt: 0.5, p: 0.5, borderRadius: 0.75, bgcolor: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.15)' }}>
                      <Typography variant="caption" sx={{ fontSize: compactMode ? '0.45rem' : '0.55rem', color: '#f59e0b' }}>
                        Ответ: "{selectedQuestion.text}"
                      </Typography>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            )}

            {/* Внутренний диалог */}
            <Box sx={{ flex: 1 }}>
              <InnerDialog 
                messages={innerDialogMessages} 
                characterName={characterName}
                isLoading={isLoading && innerDialogMessages.length === 0}
                compact={compactMode}
                showTimestamps={showTimestamps}
              />
            </Box>

            <div ref={chatEndRef} />
          </Box>

          {/* Поле ввода */}
          <Box
            sx={{
              p: compactMode ? 0.5 : 0.75,
              borderTop: '1px solid rgba(255,255,255,0.06)',
              display: 'flex',
              gap: 0.5,
              flexShrink: 0,
              bgcolor: 'rgba(0,0,0,0.15)',
              position: 'relative'
            }}
          >
            <Box sx={{ position: 'relative', flex: 1 }}>
              <TextField
                inputRef={inputRef}
                fullWidth
                size="small"
                placeholder={
                  selectedQuestion 
                    ? "Введите ответ..." 
                    : isConnected 
                      ? "Сообщение или / для подсказки..." 
                      : "Подключитесь к игре..."
                }
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  if (e.target.value.startsWith('/')) {
                    setShowSuggestions(true);
                  } else {
                    setShowSuggestions(false);
                  }
                }}
                onKeyDown={handleKeyDown}
                onFocus={() => {
                  if (input.startsWith('/')) setShowSuggestions(true);
                }}
                disabled={!isConnected || isLoading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: selectedQuestion ? 'rgba(245, 158, 11, 0.05)' : 'rgba(255,255,255,0.03)',
                    borderRadius: 1,
                    fontSize: compactMode ? '0.6rem' : '0.7rem',
                    height: compactMode ? 24 : 28,
                    '& fieldset': {
                      borderColor: selectedQuestion ? 'rgba(245, 158, 11, 0.2)' : 'rgba(255,255,255,0.05)',
                      borderWidth: 1,
                    }
                  }
                }}
              />
              <SuggestionPanel
                suggestions={SUGGESTIONS}
                onSelect={handleSelectSuggestion}
                input={input}
              />
            </Box>
            
            {selectedQuestion ? (
              <Button
                variant="contained"
                onClick={handleAnswerQuestion}
                disabled={!input.trim() || isLoading}
                sx={{
                  borderRadius: 1,
                  minWidth: compactMode ? 24 : 28,
                  width: compactMode ? 24 : 28,
                  height: compactMode ? 24 : 28,
                  p: 0,
                  bgcolor: '#f59e0b',
                  color: '#000',
                  '&:hover': { bgcolor: '#d97706' }
                }}
              >
                <SendIcon sx={{ fontSize: compactMode ? 12 : 14 }} />
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleSend}
                disabled={!input.trim() || !isConnected || isLoading}
                sx={{
                  borderRadius: 1,
                  minWidth: compactMode ? 24 : 28,
                  width: compactMode ? 24 : 28,
                  height: compactMode ? 24 : 28,
                  p: 0,
                  bgcolor: '#8b5cf6',
                  color: '#fff',
                  '&:hover': { bgcolor: '#7c3aed' },
                  '&.Mui-disabled': {
                    bgcolor: 'rgba(255,255,255,0.05)',
                    color: 'text.secondary'
                  }
                }}
              >
                {isProcessing ? (
                  <CircularProgress size={compactMode ? 12 : 14} sx={{ color: '#fff' }} />
                ) : (
                  <SendIcon sx={{ fontSize: compactMode ? 12 : 14 }} />
                )}
              </Button>
            )}
          </Box>

          {/* Подсказка по командам */}
          {isConnected && (
            <Box
              sx={{
                p: compactMode ? 0.25 : 0.5,
                borderTop: '1px solid rgba(255,255,255,0.03)',
                display: 'flex',
                gap: 0.25,
                flexWrap: 'wrap',
                bgcolor: 'rgba(0,0,0,0.08)',
                flexShrink: 0
              }}
            >
              <Typography variant="caption" sx={{ fontSize: compactMode ? '0.35rem' : '0.45rem', color: 'text.secondary', mr: 0.25 }}>
                Команды:
              </Typography>
              {SUGGESTIONS.slice(0, compactMode ? 4 : 6).map((s, i) => (
                <Chip
                  key={i}
                  label={s.cmd}
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setInput(s.cmd + ' ');
                    inputRef.current?.focus();
                  }}
                  sx={{
                    height: compactMode ? 12 : 14,
                    '& .MuiChip-label': { fontSize: compactMode ? '0.3rem' : '0.4rem', px: 0.5 },
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'rgba(139, 92, 246, 0.1)',
                      borderColor: 'rgba(139, 92, 246, 0.3)'
                    }
                  }}
                />
              ))}
              {!compactMode && (
                <Typography variant="caption" sx={{ fontSize: '0.4rem', color: 'text.secondary', ml: 'auto' }}>
                  /help для всех команд | Ctrl+/ для быстрого ввода
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default SemanticChat;